import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { List, ListItem, Typography, Button, Dialog, DialogActions, DialogContent, TextField, DialogTitle } from '@mui/material';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import { useUpdateAnswerMutation, useDeleteAnswerMutation } from '../features/api/rest/apiAnswersSlice';
import Spinner from './Spinner';
import VotingArrow from './VotingArrow';
import AnswerReport from './AnswerReport';

function AnswerCard({ answerId, answer, owner, created, questionId, totScore }) {
  const { t } = useTranslation();
  const { ean } = useParams();
  const formatDate = (dateToFormat) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    if (window.navigator.language === 'it-IT') return new Date(dateToFormat).toLocaleDateString('it-IT', options);
    return new Date(dateToFormat).toLocaleDateString('en-US', options);
  };

  const { currentData, status } = useGetProfileQuery(undefined, { refetchOnMountOrArgChange: true });

  const [updateAnswer, { isLoadingUpdate }] = useUpdateAnswerMutation();
  const [deleteAnswer, { isLoadingDelete }] = useDeleteAnswerMutation();

  const [open, setOpen] = useState(false);
  const [answerToHandle, setAnswer] = useState(answer);

  const handleAnswerClick = () => {
    setOpen(!open);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    try {
      await deleteAnswer({ ean, Q: questionId, id: answerId }).unwrap();
      window.location.reload();
    } catch (error) {
      console.log(error);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await updateAnswer({ payload: { body: answerToHandle }, ean, Q: questionId, id: answerId }).unwrap();
      window.location.reload();
    } catch (error) {
      console.log(error);
    }
  };

  if (isLoadingUpdate || isLoadingDelete) return <Spinner />;

  return (
    <List sx={{ pb: 0.5, pt: 0, ml: 5 }}>
      <ListItem sx={{ justifyContent: 'left', p: 0 }}>
        <VotingArrow questionId={questionId} answerId={answerId} totScore={totScore} typology="Answer" />
        <Typography sx={{ ml: 1, fontSize: 17 }}>{answer}</Typography>
        {status === 'fulfilled' && owner === currentData.username && <Button onClick={handleAnswerClick}>{t('questionsAnswers.handleAnswer')}</Button>}
        <AnswerReport questionId={questionId} answerId={answerId} owner={owner} />
      </ListItem>
      <Dialog open={open} onClose={handleAnswerClick} fullWidth maxWidth="sm">
        <DialogTitle>{t('questionsAnswers.handleAnswerTitle')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" id="answer" type="text" fullWidth variant="standard" required value={answerToHandle} onChange={(e) => setAnswer(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAnswerClick}>{t('common.actions.cancel')}</Button>
          <Button onClick={handleDelete}>{t('common.actions.delete')}</Button>
          <Button onClick={handleEdit}>{t('common.actions.edit')}</Button>
        </DialogActions>
      </Dialog>
      <ListItem sx={{ justifyContent: 'left', p: 0 }}>
        <Typography sx={{ ml: 1, fontSize: 17, color: 'gray' }}>{t('questionsAnswers.from', { owner, created: formatDate(created) })}</Typography>
      </ListItem>
    </List>
  );
}

AnswerCard.propTypes = {
  answer: PropTypes.string.isRequired,
  answerId: PropTypes.number.isRequired,
  created: PropTypes.string.isRequired,
  owner: PropTypes.string.isRequired,
  questionId: PropTypes.number.isRequired,
  totScore: PropTypes.number.isRequired,
};

export default AnswerCard;
